/**
 * NodeModal — fully editable modal for an adjacency node.
 *
 * Replaces the read-only NodeDetailModal for the double-click flow.
 * Collapsible sections for Identity, Strategy, Execution, Bain Scoring,
 * Relationships, and Related Links. Save/Cancel/Delete/Duplicate in footer.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronDown, ChevronRight, Trash2, Copy } from 'lucide-react';
import type {
  AdjacencyNode,
  StrategicInitiative,
  Phase,
  Status,
  AdjacencyAxis,
  RelatednessRing,
  NodeType,
  DataSource,
  UpdateNodeInput,
} from '../../types/adjacency';
import { RING_SUCCESS_RATES } from '../../types/adjacency';
import { INITIATIVE_COLORS, STATUS_COLORS, STATUS_DEFAULT_COLOR } from '../../utils/map-colors';
import { getAllPhases, getAllStatuses } from '../../utils/reference-data';
import { suggestAxes } from '../../utils/axis-suggest';
import type { AxisSuggestion } from '../../utils/axis-suggest';
import { BainScorerSection } from './BainScorerSection';
import { RelationshipSection } from './RelationshipSection';
import { RelatedLinksSection } from './RelatedLinksSection';
import { AxisChangeWarningDialog } from './AxisChangeWarningDialog';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import budgetData from '../../data/budget-2026.json';

// WQ-343: known budget project names — the same source the FTE charts' bpMap uses
const BUDGET_PROJECT_NAMES = [...new Set((budgetData.projects as { project: string }[]).map(p => p.project))].sort();

// ── Constants ──────────────────────────────────────────────────────────

const ALL_INITIATIVES: StrategicInitiative[] = [
  'US Mining', 'Proximity Detection', 'Analytics', 'Maritime',
  'International Mining', 'New Verticals', 'Capabilities', 'Other',
];


const ALL_AXES: AdjacencyAxis[] = [
  'Core', 'New Services', 'New Markets & Customers', 'Forward Integration',
  'New Technologies & Capabilities', 'New Channels', 'New Geographies',
  'Backward Integration', 'New Products',
];

const ALL_RINGS: RelatednessRing[] = [0, 1, 2, 3, 4];
const ALL_NODE_TYPES: NodeType[] = ['product', 'capability', 'market', 'candidate'];
const ALL_SOURCES: DataSource[] = ['priority-list', 'adjacency-map', 'manual'];

const SECTION_KEYS = ['identity', 'strategy', 'bain', 'execution', 'financial', 'operations', 'relationships', 'related'] as const;
type SectionKey = (typeof SECTION_KEYS)[number];

const SECTION_LABELS: Record<SectionKey, string> = {
  identity: 'Identity',
  strategy: 'Strategy',
  execution: 'Execution',
  financial: 'Financial',
  operations: 'Operations',
  bain: 'Bain Scoring',
  relationships: 'Relationships',
  related: 'Related Links',
};

const STORAGE_KEY = 'iwt-modal-sections';

function loadCollapsed(): Set<SectionKey> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return new Set(JSON.parse(raw) as SectionKey[]);
  } catch { /* ignore */ }
  return new Set();
}

function saveCollapsed(set: Set<SectionKey>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify([...set]));
}

// ── Props ──────────────────────────────────────────────────────────────

interface NodeModalProps {
  node: AdjacencyNode;
  allNodes: AdjacencyNode[];
  onClose: () => void;
  onSave: (id: string, changes: UpdateNodeInput) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
  /** Relationship actions wired to the store. */
  onAddParent: (parentId: string, childId: string) => void;
  onAddChild: (parentId: string, childId: string) => void;
  onAddRelated: (nodeA: string, nodeB: string) => void;
  onRemoveRelationship: (nodeA: string, nodeB: string) => void;
  onConvertEdgeType: (nodeA: string, nodeB: string, toType: 'parent-child' | 'related', parentId?: string) => void;
}

// ── Component ──────────────────────────────────────────────────────────

export function NodeModal({
  node,
  allNodes,
  onClose,
  onSave,
  onDelete,
  onDuplicate,
  onAddParent,
  onAddChild,
  onAddRelated,
  onRemoveRelationship,
  onConvertEdgeType,
}: NodeModalProps) {
  // ── Editable state ──────────────────────────────────────────────────
  const [name, setName] = useState(node.name);
  const [description, setDescription] = useState(node.description);
  const [initiative, setInitiative] = useState(node.strategicInitiative);
  const [phase, setPhase] = useState<Phase | null>(node.phase);
  const [status, setStatus] = useState<Status | null>(node.status);
  const [priority, setPriority] = useState<number>(node.priority ?? 0);
  const [axis, setAxis] = useState(node.adjacencyAxis);
  const [ring, setRing] = useState(node.relatednessRing);
  const [nodeType, setNodeType] = useState(node.nodeType);
  const [source, setSource] = useState(node.source);
  const [reinforcesCore, setReinforcesCore] = useState(node.reinforcesCore);
  const [sharedCustomers, setSharedCustomers] = useState(node.sharedCustomers);
  const [sharedCosts, setSharedCosts] = useState(node.sharedCosts);
  const [commonCompetitors, setCommonCompetitors] = useState(node.commonCompetitors);
  const [attractivenessScore, setAttractivenessScore] = useState(node.attractivenessScore);

  // ── Financial state ─────────────────────────────────────────────────
  const [iRnD, setIRnD] = useState<number | null>(node.iRnD);
  const [fRnD, setFRnD] = useState<number | null>(node.fRnD);
  // totalBudgetEstimate is now auto-calculated from iRnD + fRnD (no state needed)
  const [estRevenue, setEstRevenue] = useState<number | null>(node.estRevenue);
  const [estRnD, setEstRnD] = useState<number | null>(node.estRnD);
  const [tam, setTam] = useState<number | null>(node.tam);
  const [sam, setSam] = useState<number | null>(node.sam);
  const [budgetProjectName, setBudgetProjectName] = useState<string | null>(node.budgetProjectName);

  // ── Operations state ────────────────────────────────────────────────
  const [startDate, setStartDate] = useState<string | null>(node.startDate);
  const [endDate, setEndDate] = useState<string | null>(node.endDate);
  const [mrdDate, setMrdDate] = useState<string | null>(node.mrdDate);
  const [atdDate, setAtdDate] = useState<string | null>(node.atdDate);
  const [atqDate, setAtqDate] = useState<string | null>(node.atqDate);
  const [atsDate, setAtsDate] = useState<string | null>(node.atsDate);
  const [devLane, setDevLane] = useState<string | null>(node.devLane);
  const [devTeam, setDevTeam] = useState<string | null>(node.devTeam);
  const [productManager, setProductManager] = useState<string | null>(node.productManager);
  const [productLine, setProductLine] = useState<string | null>(node.productLine);

  // ── Dynamic Phase/Status options (includes any SP-introduced values) ──
  const phaseOptions = useMemo(() => getAllPhases(allNodes), [allNodes]);
  const statusOptions = useMemo(() => getAllStatuses(allNodes), [allNodes]);

  // ── Axis suggestions ────────────────────────────────────────────────
  const axisSuggestions: AxisSuggestion[] = useMemo(
    () => suggestAxes(name, description),
    [name, description],
  );

  // ── Section collapse ────────────────────────────────────────────────
  const [collapsed, setCollapsed] = useState<Set<SectionKey>>(loadCollapsed);

  const toggleSection = useCallback((key: SectionKey) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      saveCollapsed(next);
      return next;
    });
  }, []);

  // ── Axis warning state ───────────────────────────────────────────────
  const [pendingAxisWarning, setPendingAxisWarning] = useState<{
    changes: UpdateNodeInput;
    mismatches: { targetId: string; targetNodeName: string; currentType: 'parent-child' | 'related'; proposedType: 'parent-child' | 'related' }[];
  } | null>(null);

  // ── Derived ─────────────────────────────────────────────────────────
  const initiativeColor = INITIATIVE_COLORS[initiative];
  const statusColor = status ? STATUS_COLORS[status] : STATUS_DEFAULT_COLOR;
  // ── Handlers ────────────────────────────────────────────────────────
  const buildChanges = useCallback((): UpdateNodeInput => ({
    name: name.trim() || node.name,
    description,
    strategicInitiative: initiative,
    phase,
    status,
    priority,
    adjacencyAxis: axis,
    relatednessRing: ring,
    nodeType,
    source,
    reinforcesCore,
    sharedCustomers,
    sharedCosts,
    commonCompetitors,
    attractivenessScore,
    iRnD,
    fRnD,
    totalBudgetEstimate: ((iRnD ?? 0) + (fRnD ?? 0)) > 0 ? (iRnD ?? 0) + (fRnD ?? 0) : null,
    estRevenue,
    estRnD,
    tam,
    sam,
    budgetProjectName: budgetProjectName?.trim() || null,
    startDate,
    endDate,
    mrdDate,
    atdDate,
    atqDate,
    atsDate,
    devLane,
    devTeam,
    productManager,
    productLine,
  }), [
    name, description, initiative, phase, status, priority, axis, ring,
    nodeType, source, reinforcesCore, sharedCustomers, sharedCosts,
    commonCompetitors, attractivenessScore, node.name,
    iRnD, fRnD, estRevenue, estRnD, tam, sam, budgetProjectName,
    startDate, endDate, mrdDate, atdDate, atqDate, atsDate,
    devLane, devTeam, productManager, productLine,
  ]);

  const handleSave = useCallback(() => {
    const changes = buildChanges();
    const liveRef = allNodes.find(n => n.id === node.id) ?? node;

    // Check if axis changed and node has edges
    if (axis !== node.adjacencyAxis && liveRef.edges.length > 0) {
      const mismatches: typeof pendingAxisWarning extends null ? never : NonNullable<typeof pendingAxisWarning>['mismatches'] = [];

      for (const edge of liveRef.edges) {
        const targetNode = allNodes.find(n => n.id === edge.targetId);
        if (!targetNode) continue;

        const isParentChild = edge.relationship === 'parent' || edge.relationship === 'child';
        const isPeer = edge.relationship === 'peer';
        const targetOnSameAxis = targetNode.adjacencyAxis === axis;

        if (isParentChild && !targetOnSameAxis) {
          // Parent-child now spans different axes → should be related
          mismatches.push({
            targetId: edge.targetId,
            targetNodeName: targetNode.name,
            currentType: 'parent-child',
            proposedType: 'related',
          });
        } else if (isPeer && targetOnSameAxis) {
          // Related/peer now on same axis → should be parent-child
          mismatches.push({
            targetId: edge.targetId,
            targetNodeName: targetNode.name,
            currentType: 'related',
            proposedType: 'parent-child',
          });
        }
      }

      if (mismatches.length > 0) {
        setPendingAxisWarning({ changes, mismatches });
        return;
      }
    }

    onSave(node.id, changes);
    onClose();
  }, [buildChanges, axis, node, allNodes, onSave, onClose]);

  const handleAxisWarningContinue = useCallback(() => {
    if (!pendingAxisWarning) return;
    const { changes, mismatches } = pendingAxisWarning;
    const liveRef = allNodes.find(n => n.id === node.id) ?? node;

    // Save the node first
    onSave(node.id, changes);

    // Convert each mismatched edge
    for (const m of mismatches) {
      if (m.proposedType === 'parent-child') {
        // Determine parent by ring: lower ring = parent. Same ring → edited node is parent.
        const targetNode = allNodes.find(n => n.id === m.targetId);
        const parentId = targetNode && targetNode.relatednessRing < liveRef.relatednessRing
          ? m.targetId
          : node.id;
        onConvertEdgeType(node.id, m.targetId, 'parent-child', parentId);
      } else {
        onConvertEdgeType(node.id, m.targetId, 'related');
      }
    }

    setPendingAxisWarning(null);
    onClose();
  }, [pendingAxisWarning, node, allNodes, onSave, onClose, onConvertEdgeType]);

  const handleAxisWarningCancel = useCallback(() => {
    setPendingAxisWarning(null);
  }, []);

  const [pendingDelete, setPendingDelete] = useState(false);

  const handleDelete = useCallback(() => {
    setPendingDelete(true);
  }, []);

  const confirmDelete = useCallback(() => {
    onDelete(node.id);
    onClose();
  }, [node.id, onDelete, onClose]);

  const handleDuplicate = useCallback(() => {
    onDuplicate(node.id);
    onClose();
  }, [node.id, onDuplicate, onClose]);

  const handleBainChange = useCallback((field: 'sharedCustomers' | 'sharedCosts' | 'commonCompetitors', value: number) => {
    if (field === 'sharedCustomers') setSharedCustomers(value);
    else if (field === 'sharedCosts') setSharedCosts(value);
    else setCommonCompetitors(value);
  }, []);

  // ── Keyboard shortcuts ──────────────────────────────────────────────
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSave();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, handleSave]);

  // Use the live node from allNodes to reflect real-time relationship changes
  const liveNode = allNodes.find(n => n.id === node.id) ?? node;

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[1100]" style={{ background: 'var(--modal-backdrop)' }} onClick={onClose} />

      {/* Modal */}
      <div
        role="dialog"
        aria-modal="true"
        className="fixed top-4 left-1/2 z-[1200] w-[420px] max-h-[calc(100%-2rem)] flex flex-col rounded-2xl"
        style={{
          transform: 'translateX(-50%)',
          background: 'var(--modal-bg)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          border: `1px solid ${initiativeColor}33`,
          boxShadow: `var(--modal-shadow), 0 0 40px ${initiativeColor}15`,
        }}
      >
        {/* ── Header ─────────────────────────────────────── */}
        <div className="flex items-start justify-between p-4 pb-3 shrink-0" style={{ borderBottom: '1px solid var(--glass-border)' }}>
          <div className="flex-1 min-w-0">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full text-sm font-semibold outline-none rounded px-1.5 py-1 transition-colors"
              style={{
                color: 'var(--text-primary)',
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid var(--glass-border)',
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(59,130,246,0.5)'; e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--glass-border)'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
              placeholder="Node name..."
            />
            <div className="flex items-center gap-2 mt-1">
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium"
                style={{ background: initiativeColor + '33', color: initiativeColor, border: `1px solid ${initiativeColor}44` }}
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: initiativeColor }} />
                {initiative}
              </span>
              {status && (
                <span className="flex items-center gap-1 text-[10px]" style={{ color: 'var(--text-muted)' }}>
                  <span className="w-2 h-2 rounded-full" style={{ background: statusColor }} />
                  {status}
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded ml-2 shrink-0" style={{ color: 'var(--text-muted)' }}>
            <X size={16} />
          </button>
        </div>

        {/* ── Scrollable body ────────────────────────────── */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">

          {/* Identity Section */}
          <CollapsibleSection sectionKey="identity" collapsed={collapsed} onToggle={toggleSection}>
            <div className="space-y-2">
              <FieldGroup label="Description">
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-1.5 rounded-lg text-[11px] resize-none"
                  style={{
                    background: 'var(--glass-bg)',
                    border: '1px solid var(--glass-border)',
                    color: 'var(--text-secondary)',
                    outline: 'none',
                  }}
                />
              </FieldGroup>
              <div className="grid grid-cols-2 gap-2">
                <FieldGroup label="Node Type">
                  <SelectField value={nodeType} onChange={(v) => setNodeType(v as NodeType)} options={ALL_NODE_TYPES} />
                </FieldGroup>
                <FieldGroup label="Source">
                  <SelectField value={source} onChange={(v) => setSource(v as DataSource)} options={ALL_SOURCES} />
                </FieldGroup>
              </div>
            </div>
          </CollapsibleSection>

          {/* Strategy Section */}
          <CollapsibleSection sectionKey="strategy" collapsed={collapsed} onToggle={toggleSection}>
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <FieldGroup label="Initiative">
                  <SelectField value={initiative} onChange={(v) => setInitiative(v as StrategicInitiative)} options={ALL_INITIATIVES} />
                </FieldGroup>
                <FieldGroup label="Axis">
                  <SelectField value={axis} onChange={(v) => setAxis(v as AdjacencyAxis)} options={ALL_AXES} />
                </FieldGroup>
              </div>
              {axisSuggestions.length > 0 && (
                <div>
                  <div className="text-[9px] uppercase tracking-wide mb-1" style={{ color: 'var(--text-muted)' }}>
                    Suggested Axes
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {axisSuggestions.map(s => (
                      <button
                        key={s.axis}
                        type="button"
                        onClick={() => setAxis(s.axis)}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] transition-colors"
                        style={{
                          background: s.axis === axis ? 'rgba(56,189,248,0.25)' : 'var(--glass-bg)',
                          border: `1px solid ${s.axis === axis ? 'rgba(56,189,248,0.5)' : 'var(--glass-border)'}`,
                          color: s.axis === axis ? '#38bdf8' : 'var(--text-secondary)',
                          cursor: 'pointer',
                        }}
                      >
                        {s.axis}
                        <span style={{ color: 'var(--text-muted)', fontSize: '9px' }}>{s.confidence}%</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <FieldGroup label="Ring">
                  <select
                    value={ring}
                    onChange={(e) => setRing(Number(e.target.value) as RelatednessRing)}
                    className="w-full px-2 py-1 rounded-lg text-[11px] appearance-none"
                    style={{
                      background: 'var(--glass-bg)',
                      border: '1px solid var(--glass-border)',
                      color: 'var(--text-secondary)',
                      cursor: 'pointer',
                    }}
                  >
                    {ALL_RINGS.map(r => (
                      <option key={r} value={r}>
                        {r} — {RING_SUCCESS_RATES[r].label} ({RING_SUCCESS_RATES[r].rate})
                      </option>
                    ))}
                  </select>
                </FieldGroup>
                <FieldGroup label="Reinforces Core">
                  <label className="flex items-center gap-2 cursor-pointer py-1">
                    <input
                      type="checkbox"
                      checked={reinforcesCore}
                      onChange={(e) => setReinforcesCore(e.target.checked)}
                      className="rounded"
                    />
                    <span className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>
                      {reinforcesCore ? 'Yes' : 'No'}
                    </span>
                  </label>
                </FieldGroup>
              </div>
            </div>
          </CollapsibleSection>

          {/* Bain Scoring Section */}
          <CollapsibleSection sectionKey="bain" collapsed={collapsed} onToggle={toggleSection}>
            <BainScorerSection
              sharedCustomers={sharedCustomers}
              sharedCosts={sharedCosts}
              commonCompetitors={commonCompetitors}
              onChange={handleBainChange}
            />

            {/* Attractiveness — independent from relatedness */}
            <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--glass-border)' }}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>Attractiveness</span>
                <span className="text-[11px] font-medium" style={{ color: 'var(--text-primary)' }}>
                  {attractivenessScore}/10
                </span>
              </div>
              <input
                type="range"
                min={1}
                max={10}
                step={1}
                value={attractivenessScore}
                onChange={(e) => setAttractivenessScore(Number(e.target.value))}
                className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #f59e0b ${((attractivenessScore - 1) / 9) * 100}%, rgba(100,116,139,0.3) ${((attractivenessScore - 1) / 9) * 100}%)`,
                }}
              />
              <div className="text-[9px] mt-1" style={{ color: 'var(--text-dim)' }}>
                Market size · Growth · Differentiation · Profitability · Leadership opportunity
              </div>
            </div>
          </CollapsibleSection>

          {/* Execution Section */}
          <CollapsibleSection sectionKey="execution" collapsed={collapsed} onToggle={toggleSection}>
            <div className="grid grid-cols-3 gap-3">
              <FieldGroup label="Phase">
                <select
                  value={phase ?? ''}
                  onChange={(e) => setPhase(e.target.value ? e.target.value as Phase : null)}
                  className="w-full min-w-0 px-2 py-1 rounded-lg text-[11px] appearance-none"
                  style={{
                    background: 'var(--glass-bg)',
                    border: '1px solid var(--glass-border)',
                    color: 'var(--text-secondary)',
                    cursor: 'pointer',
                  }}
                >
                  <option value="">None</option>
                  {phaseOptions.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </FieldGroup>
              <FieldGroup label="Status">
                <select
                  value={status ?? ''}
                  onChange={(e) => setStatus(e.target.value ? e.target.value as Status : null)}
                  className="w-full min-w-0 px-2 py-1 rounded-lg text-[11px] appearance-none"
                  style={{
                    background: 'var(--glass-bg)',
                    border: '1px solid var(--glass-border)',
                    color: 'var(--text-secondary)',
                    cursor: 'pointer',
                  }}
                >
                  <option value="">None</option>
                  {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </FieldGroup>
              <FieldGroup label="Priority">
                <input
                  type="number"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value ? Number(e.target.value) : 0)}
                  min={0}
                  placeholder="0"
                  className="w-full min-w-0 px-2 py-1 rounded-lg text-[11px]"
                  style={{
                    background: 'var(--glass-bg)',
                    border: '1px solid var(--glass-border)',
                    color: 'var(--text-secondary)',
                    outline: 'none',
                  }}
                />
              </FieldGroup>
            </div>
          </CollapsibleSection>

          {/* Financial Section */}
          <CollapsibleSection sectionKey="financial" collapsed={collapsed} onToggle={toggleSection}>
            <div className="grid grid-cols-2 gap-2">
              <FieldGroup label="iR&D ($)">
                <CurrencyInput value={iRnD} onChange={setIRnD} />
              </FieldGroup>
              <FieldGroup label="FR&D ($)">
                <CurrencyInput value={fRnD} onChange={setFRnD} />
              </FieldGroup>
              <FieldGroup label="Total Budget Est ($)">
                <div className="w-full px-2 py-1 rounded-lg text-[11px] tabular-nums" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--glass-border)', color: 'var(--text-muted)' }}>
                  {((iRnD ?? 0) + (fRnD ?? 0)) > 0 ? `${((iRnD ?? 0) + (fRnD ?? 0)).toLocaleString()}` : '—'}
                </div>
              </FieldGroup>
              <FieldGroup label="Est Revenue ($)">
                <CurrencyInput value={estRevenue} onChange={setEstRevenue} />
              </FieldGroup>
              <FieldGroup label="Est R&D — SP (Ignored)">
                <CurrencyInput value={estRnD} onChange={setEstRnD} />
              </FieldGroup>
              <FieldGroup label="TAM ($)">
                <CurrencyInput value={tam} onChange={setTam} />
              </FieldGroup>
              <FieldGroup label="SAM ($)">
                <CurrencyInput value={sam} onChange={setSam} />
              </FieldGroup>
              <div className="col-span-2">
                <FieldGroup label="Budget project">
                  <div className="flex items-center gap-1">
                    <input
                      type="text"
                      list="node-modal-budget-projects"
                      aria-label="Budget project"
                      value={budgetProjectName ?? ''}
                      onChange={(e) => setBudgetProjectName(e.target.value || null)}
                      placeholder="Not linked"
                      className="w-full px-2 py-1 rounded-lg text-[11px]"
                      style={INPUT_STYLE}
                    />
                    {budgetProjectName && (
                      <button
                        type="button"
                        aria-label="Clear budget project"
                        title="Unlink budget project"
                        onClick={() => setBudgetProjectName(null)}
                        className="p-1 rounded-md"
                        style={{ color: 'var(--text-muted)' }}
                      >
                        <X size={12} />
                      </button>
                    )}
                  </div>
                  <datalist id="node-modal-budget-projects">
                    {BUDGET_PROJECT_NAMES.map((p) => <option key={p} value={p} />)}
                  </datalist>
                </FieldGroup>
              </div>
            </div>
          </CollapsibleSection>

          {/* Operations Section */}
          <CollapsibleSection sectionKey="operations" collapsed={collapsed} onToggle={toggleSection}>
            <div className="grid grid-cols-2 gap-2">
              <FieldGroup label="Dev Lane">
                <TextInput value={devLane} onChange={setDevLane} placeholder="e.g. Firmware" />
              </FieldGroup>
              <FieldGroup label="Dev Team">
                <TextInput value={devTeam} onChange={setDevTeam} placeholder="e.g. Alpha" />
              </FieldGroup>
              <FieldGroup label="Product Manager">
                <TextInput value={productManager} onChange={setProductManager} placeholder="e.g. Jane D." />
              </FieldGroup>
              <FieldGroup label="Product Line">
                <TextInput value={productLine} onChange={setProductLine} placeholder="e.g. Guardian" />
              </FieldGroup>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-2">
              <FieldGroup label="Start Date">
                <DateInput value={startDate} onChange={setStartDate} />
              </FieldGroup>
              <FieldGroup label="End Date">
                <DateInput value={endDate} onChange={setEndDate} />
              </FieldGroup>
              <FieldGroup label="ATD">
                <DateInput value={atdDate} onChange={setAtdDate} />
              </FieldGroup>
              <FieldGroup label="ATQ">
                <DateInput value={atqDate} onChange={setAtqDate} />
              </FieldGroup>
              <FieldGroup label="ATS">
                <DateInput value={atsDate} onChange={setAtsDate} />
              </FieldGroup>
              <FieldGroup label="MRD">
                <DateInput value={mrdDate} onChange={setMrdDate} />
              </FieldGroup>
            </div>
          </CollapsibleSection>

          {/* Relationships Section */}
          <CollapsibleSection sectionKey="relationships" collapsed={collapsed} onToggle={toggleSection}>
            <RelationshipSection
              node={liveNode}
              allNodes={allNodes}
              onAddParent={(parentId) => onAddParent(parentId, node.id)}
              onAddChild={(childId) => onAddChild(node.id, childId)}
              onRemove={(targetId) => onRemoveRelationship(node.id, targetId)}
            />
          </CollapsibleSection>

          {/* Related Links Section */}
          <CollapsibleSection sectionKey="related" collapsed={collapsed} onToggle={toggleSection}>
            <RelatedLinksSection
              node={liveNode}
              allNodes={allNodes}
              onAdd={(targetId) => onAddRelated(node.id, targetId)}
              onRemove={(targetId) => onRemoveRelationship(node.id, targetId)}
            />
          </CollapsibleSection>
        </div>

        {/* ── Footer ─────────────────────────────────────── */}
        <div className="flex items-center gap-2 p-3 shrink-0" style={{ borderTop: '1px solid var(--glass-border)' }}>
          <button
            onClick={handleDelete}
            className="inline-flex items-center gap-1 text-[10px] px-2.5 py-1.5 rounded-lg transition-colors"
            style={{
              background: 'rgba(239,68,68,0.12)',
              border: '1px solid rgba(239,68,68,0.3)',
              color: '#ef4444',
            }}
            title="Delete node"
          >
            <Trash2 size={11} />
            Delete
          </button>
          <button
            onClick={handleDuplicate}
            className="inline-flex items-center gap-1 text-[10px] px-2.5 py-1.5 rounded-lg transition-colors"
            style={{
              background: 'var(--glass-bg)',
              border: '1px solid var(--glass-border)',
              color: 'var(--text-secondary)',
            }}
            title="Duplicate node"
          >
            <Copy size={11} />
            Duplicate
          </button>

          <div className="flex-1" />

          <button
            onClick={onClose}
            className="text-[11px] px-3 py-1.5 rounded-lg transition-colors"
            style={{
              background: 'var(--glass-bg)',
              border: '1px solid var(--glass-border)',
              color: 'var(--text-secondary)',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="text-[11px] px-3 py-1.5 rounded-lg transition-colors"
            style={{
              background: 'rgba(56,189,248,0.2)',
              border: '1px solid rgba(56,189,248,0.4)',
              color: '#38bdf8',
            }}
            title="Ctrl+Enter"
          >
            Save
          </button>
        </div>
      </div>

      {/* ── Axis change warning dialog ────────────────── */}
      {pendingAxisWarning && (
        <AxisChangeWarningDialog
          nodeName={name.trim() || node.name}
          newAxis={axis}
          affectedEdges={pendingAxisWarning.mismatches.map(m => ({
            targetNodeName: m.targetNodeName,
            currentType: m.currentType,
            proposedType: m.proposedType,
          }))}
          onContinue={handleAxisWarningContinue}
          onCancel={handleAxisWarningCancel}
        />
      )}

      {/* ── Delete confirmation dialog ────────────────── */}
      {pendingDelete && (
        <ConfirmDialog
          title="Delete Node"
          message={`Delete "${node.name}"? You can undo this with Ctrl+Z.`}
          onConfirm={confirmDelete}
          onCancel={() => setPendingDelete(false)}
        />
      )}
    </>,
    document.body,
  );
}

// ── Helpers ────────────────────────────────────────────────────────────

function CollapsibleSection({
  sectionKey,
  collapsed,
  onToggle,
  children,
}: {
  sectionKey: SectionKey;
  collapsed: Set<SectionKey>;
  onToggle: (key: SectionKey) => void;
  children: React.ReactNode;
}) {
  const isCollapsed = collapsed.has(sectionKey);
  const Icon = isCollapsed ? ChevronRight : ChevronDown;

  return (
    <div className="rounded-lg" style={{ border: '1px solid var(--glass-border)' }}>
      <button
        onClick={() => onToggle(sectionKey)}
        className="w-full flex items-center gap-1.5 px-3 py-2 text-left"
        style={{ color: 'var(--text-primary)' }}
      >
        <Icon size={12} style={{ color: 'var(--text-muted)' }} />
        <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: 'var(--text-primary)' }}>
          {SECTION_LABELS[sectionKey]}
        </span>
      </button>
      {!isCollapsed && (
        <div className="px-3 pb-3">
          {children}
        </div>
      )}
    </div>
  );
}

function FieldGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wide mb-1" style={{ color: 'var(--text-muted)' }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function SelectField({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: readonly string[] }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-2 py-1 rounded-lg text-[11px] appearance-none"
      style={{
        background: 'var(--glass-bg)',
        border: '1px solid var(--glass-border)',
        color: 'var(--text-secondary)',
        cursor: 'pointer',
      }}
    >
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

const INPUT_STYLE = {
  background: 'var(--glass-bg)',
  border: '1px solid var(--glass-border)',
  color: 'var(--text-secondary)',
  outline: 'none',
} as const;

function CurrencyInput({ value, onChange }: { value: number | null; onChange: (v: number | null) => void }) {
  return (
    <input
      type="number"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
      placeholder="—"
      className="w-full px-2 py-1 rounded-lg text-[11px] tabular-nums"
      style={INPUT_STYLE}
    />
  );
}

function TextInput({ value, onChange, placeholder }: { value: string | null; onChange: (v: string | null) => void; placeholder?: string }) {
  return (
    <input
      type="text"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
      placeholder={placeholder ?? '—'}
      className="w-full px-2 py-1 rounded-lg text-[11px]"
      style={INPUT_STYLE}
    />
  );
}

function DateInput({ value, onChange }: { value: string | null; onChange: (v: string | null) => void }) {
  return (
    <input
      type="date"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
      className="w-full px-2 py-1 rounded-lg text-[11px]"
      style={{ ...INPUT_STYLE, colorScheme: 'var(--modal-color-scheme, dark)' }}
    />
  );
}
